// floor (catalog-outline.md "floor"; ops parity api-commerce/tables.md, backoffice pages/floor-plans.md): floor plans
// with their items, zones and free tables, as location config written at the node or in the cloud (ADR-015). A table
// is a floor-plan item whose shape group is "tables". Which order sits at a table is the order's own `tableId`
// (`orders.seatAtTable`); a table reads occupied while that order is live, so occupancy can never drift from the
// orders (tables.md Q-F10) and needs no reset on read (Q-F8).
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { FloorPlanId, FreeTableId, LocationId, TableId, ZoneId } from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten, ProposalRequired } from '../config/index.ts';

const Name = z.string().trim().min(1).max(255);

/**
 * One shape on a plan (legacy `floor_plan_items`). `geometry` is the canvas shape as the editor draws it (legacy
 * `konva_config`: x, y, width, height, rotation, radius, fill, …); null when the item has none.
 */
export const FloorPlanItem = z
  .object({
    itemId: TableId,
    name: z.string().max(255).nullable(),
    /** 0-999, unique within the zone across the location; only tables carry one. */
    tableNumber: z.int().min(0).max(999).nullable(),
    /** Required on a table. */
    zoneId: ZoneId.nullable(),
    /** `tables` (seatable), or labels, obstacles, decorations… */
    shapeGroup: z.string().trim().min(1).max(255),
    /** e.g. `table-rounded`, `table-rectangle`. */
    shapeType: z.string().trim().min(1).max(255),
    description: z.string().max(1000),
    label: z.string().max(255),
    color: z.string().max(32),
    enabled: z.boolean(),
    geometry: z.record(z.string(), z.unknown()).nullable(),
  })
  .meta({ id: 'FloorPlanItem' });

/** Legacy `floor_plans.status`, named for its axis. */
export const FloorPlanState = z.enum(['active', 'idle']).meta({ id: 'FloorPlanState' });

export const FloorPlanFields = z
  .object({
    name: Name,
    sectionCount: z.int().min(0).max(1000),
    color: z.string().max(32).nullable(),
    enabled: z.boolean(),
    planState: FloorPlanState,
    sortKey: z.string().min(1).max(64),
    /** The plan's whole item set; a save replaces it (legacy bulk save, R-F6). */
    items: z.array(FloorPlanItem).max(500),
  })
  .meta({ id: 'FloorPlanFields' });

export const FloorPlanView = FloorPlanFields.extend({ floorPlanId: FloorPlanId, locationId: LocationId.nullable(), version: z.int().nonnegative() })
  .meta({ id: 'FloorPlanView' });

/** Legacy `zones`: a table-number prefix (`P` for Patio); numbers are unique per zone. */
export const ZoneFields = z
  .object({ prefix: z.string().trim().min(1).max(10), label: z.string().trim().min(1).max(255) })
  .meta({ id: 'ZoneFields' });

export const ZoneView = ZoneFields.extend({ zoneId: ZoneId, locationId: LocationId.nullable(), version: z.int().nonnegative() }).meta({ id: 'ZoneView' });

/** Legacy `free_tables`: a table number with no shape on a plan (QR table codes at a location with no floor plan). */
export const FreeTableFields = z
  .object({ zoneId: ZoneId.nullable(), tableNumber: z.int().min(0).max(999) })
  .meta({ id: 'FreeTableFields' });

export const FreeTableView = FreeTableFields.extend({ freeTableId: FreeTableId, locationId: LocationId.nullable(), version: z.int().nonnegative() })
  .meta({ id: 'FreeTableView' });

// ---- errors ----------------------------------------------------------------------------------------------------------
/** A save core refuses: a table without a zone, a number taken in its zone, a prefix taken at the location. */
export const FloorInvalid = defineError({
  code: 'floor.invalid', httpStatus: 422, message: 'errors.floor.invalid', retryable: false,
  details: z.object({ rule: z.string(), field: z.string().optional() }),
});

export const FloorEntityNotFound = defineError({
  code: 'floor.not_found', httpStatus: 404, message: 'errors.floor.not_found', retryable: false,
  details: z.object({ entity: z.string(), id: z.string() }),
});

const configErrors = [FloorInvalid, CloudAuthoritative, ProposalRequired, FloorEntityNotFound] as const;
const common = { availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], errors: configErrors, dryRun: true } as const;
const deleted = <T extends z.ZodType>(id: T) => z.object({ id, deleted: z.literal(true) });

// ---- commands --------------------------------------------------------------------------------------------------------
export const UpsertFloorPlan = defineCommand({
  ...common,
  name: 'floor.upsertFloorPlan',
  summary: 'Create a floor plan or change it: its name, whether it is enabled, and its whole set of tables and shapes.',
  input: z.object({ floorPlanId: FloorPlanId, locationId: LocationId, changes: FloorPlanFields.partial() }),
  output: FloorPlanView,
  permission: 'floor.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['POST /v1/floor-plans', 'PUT /v1/floor-plans/{id}', 'PUT /v1/floor-plans/{id}/status', 'POST /v1/floor-plans/{id}/items'],
});

export const DeleteFloorPlan = defineCommand({
  ...common,
  name: 'floor.deleteFloorPlan',
  summary: 'Delete a floor plan with its tables; orders seated there keep their table until they close (Q-F3).',
  input: z.object({ floorPlanId: FloorPlanId, locationId: LocationId }),
  output: deleted(FloorPlanId),
  permission: 'floor.manage',
  risk: 'medium',
  agent: { expose: false },
  legacy: ['DELETE /v1/floor-plans/{id}'],
});

export const UpsertZone = defineCommand({
  ...common,
  name: 'floor.upsertZone',
  summary: 'Create a table zone (a number prefix such as "P" for Patio) or change it.',
  input: z.object({ zoneId: ZoneId, locationId: LocationId, changes: ZoneFields.partial() }),
  output: ZoneView,
  permission: 'floor.manage',
  risk: 'low',
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['POST /v1/zones', 'PUT /v1/zones/{id}', 'POST /v1/zones/validate'],
});

export const UpsertFreeTable = defineCommand({
  ...common,
  name: 'floor.upsertFreeTable',
  summary: 'Add a table number with no place on a floor plan (for QR table codes), or change it.',
  input: z.object({ freeTableId: FreeTableId, locationId: LocationId, changes: FreeTableFields.partial() }),
  output: FreeTableView,
  permission: 'floor.manage',
  risk: 'low',
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['POST /v1/free-tables'],
});

export const DeleteFreeTable = defineCommand({
  ...common,
  name: 'floor.deleteFreeTable',
  summary: 'Delete a free table.',
  input: z.object({ freeTableId: FreeTableId, locationId: LocationId }),
  output: deleted(FreeTableId),
  permission: 'floor.manage',
  risk: 'low',
  agent: { expose: false },
  legacy: ['DELETE /v1/free-tables/{id}'],
});

// ---- queries ---------------------------------------------------------------------------------------------------------
const byLocation = z.object({ locationId: LocationId });
const listQuery = {
  availability: 'location', scope: 'location', errors: [], agent: { expose: true, surfaces: ['backoffice.assistant'] }, permission: { session: 'user' },
} as const;

export const ListFloorPlans = defineQuery({
  // Legacy's back office floor plans sit behind manage_floor_plans, as the Floor Plans page does (lane P2, item 6).
  ...listQuery, permission: 'floor.manage', name: 'floor.floorPlans.list', summary: "The location's floor plans with their tables and shapes.", input: byLocation,
  output: z.object({ floorPlans: z.array(FloorPlanView) }), legacy: ['GET /v1/floor-plans', 'GET /v2/floor-plans', 'GET /v1/floor-plans/floor-plan-items'],
});
export const ListZones = defineQuery({
  ...listQuery, name: 'floor.zones.list', summary: "The location's table zones, by prefix.", input: byLocation,
  output: z.object({ zones: z.array(ZoneView) }), legacy: ['GET /v1/zones'],
});
export const ListFreeTables = defineQuery({
  ...listQuery, name: 'floor.freeTables.list', summary: "The location's free tables, by number.", input: byLocation,
  output: z.object({ freeTables: z.array(FreeTableView) }), legacy: ['GET /v1/free-tables'],
});

export const floorCommands = [UpsertFloorPlan, DeleteFloorPlan, UpsertZone, UpsertFreeTable, DeleteFreeTable] as const;
export const floorQueries = [ListFloorPlans, ListZones, ListFreeTables] as const;
